import { User } from '../types/alarm';
import { getLicenseLimits } from '../config/licenseLimits';

// config에서 가져온 함수 재export
export { getLicenseLimits };

export type AssignmentType = 'general' | 'routine' | 'project';

/**
 * 클래스 생성 제한 체크
 */
export const checkClassLimit = (
  user: User,
  currentClassCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxClasses === null) return { allowed: true };
  
  if (currentClassCount >= limits.maxClasses) {
    return {
      allowed: false,
      message: `최대 ${limits.maxClasses}개 클래스까지만 생성할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/**
 * 학생 추가 제한 체크
 */
export const checkStudentLimit = (
  user: User,
  currentStudentCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxStudentsPerClass === null) return { allowed: true };
  
  if (currentStudentCount >= limits.maxStudentsPerClass) {
    return {
      allowed: false,
      message: `클래스당 최대 ${limits.maxStudentsPerClass}명까지만 추가할 수 있습니다.`,
    };
  }
  return { allowed: true };
};

/**
 * 과제 생성 제한 체크 (월별)
 */
export const checkAssignmentLimit = (
  user: User,
  currentMonthAssignmentCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxAssignmentsPerMonth === null) return { allowed: true };
  
  if (currentMonthAssignmentCount >= limits.maxAssignmentsPerMonth) {
    return {
      allowed: false,
      message: `월 최대 ${limits.maxAssignmentsPerMonth}개 과제까지만 생성할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/**
 * 저장 공간 제한 체크
 */
export const checkStorageLimit = (
  user: User,
  currentStorageMB: number,
  additionalMB: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxStorageMB === null) return { allowed: true };
  
  const totalStorage = currentStorageMB + additionalMB;
  if (totalStorage > limits.maxStorageMB) {
    const availableMB = Math.max(0, limits.maxStorageMB - currentStorageMB);
    return {
      allowed: false,
      message: `저장 공간이 부족합니다. (사용 가능: ${availableMB}MB / 최대: ${limits.maxStorageMB}MB) 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/**
 * 과제 타입 제한 체크
 */
export const checkAssignmentTypeLimit = (
  user: User,
  assignmentType: AssignmentType
): { allowed: boolean; message?: string } => {
  switch (user.userType) {
    case 'free':
      // 프리: 일반과제만
      if (assignmentType !== 'general') {
        return {
          allowed: false,
          message: `프리 플랜에서는 일반과제만 생성할 수 있습니다. ${assignmentType === 'routine' ? '루틴' : '프로젝트'} 과제를 사용하려면 이용권을 업그레이드하세요.`,
        };
      }
      break;
    case 'basic':
      // 베이직: 일반과제, 루틴과제만
      if (assignmentType === 'project') {
        return {
          allowed: false,
          message: '베이직 플랜에서는 프로젝트 과제를 생성할 수 없습니다. 프로젝트 과제를 사용하려면 이용권을 업그레이드하세요.',
        };
      }
      break;
    case 'standard':
    case 'pro':
    case 'admin':
      // 스탠다드, 프로, 관리자: 모든 과제 타입 가능
      break;
    default:
      // 기본값은 프리와 동일하게 제한
      if (assignmentType !== 'general') {
        return {
          allowed: false,
          message: `일반과제만 생성할 수 있습니다. ${assignmentType === 'routine' ? '루틴' : '프로젝트'} 과제를 사용하려면 이용권을 업그레이드하세요.`,
        };
      }
  }
  return { allowed: true };
};

/**
 * 클래스챗 사용 가능 여부 체크
 */
export const checkChatAccess = (user: User): { allowed: boolean; message?: string } => {
  if (user.userType === 'free' || user.userType === 'basic') {
    return {
      allowed: false,
      message: '클래스챗 기능은 스탠다드 플랜 이상에서 사용할 수 있습니다. 이용권을 업그레이드하세요.',
    };
  }
  return { allowed: true };
};

/**
 * 문제 세트 수 제한 체크
 */
export const checkQuizSetLimit = (
  user: User,
  currentSetCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxQuizSets === null) return { allowed: true };
  
  if (currentSetCount >= limits.maxQuizSets) {
    return {
      allowed: false,
      message: `최대 ${limits.maxQuizSets}개 문제 세트까지만 생성할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/**
 * 문제 카드 수 제한 체크 (세트당)
 */
export const checkQuizCardLimit = (
  user: User,
  currentCardCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxQuizCardsPerSet === null) return { allowed: true };
  
  if (currentCardCount >= limits.maxQuizCardsPerSet) {
    return {
      allowed: false,
      message: `문제 세트당 최대 ${limits.maxQuizCardsPerSet}개 문제 카드까지만 추가할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/** 프로 플랜 이상(프로·관리자) 여부 */
export const isProPlanOrAbove = (userType?: string): boolean =>
  userType === 'pro' || userType === 'admin';

/** 스탠다드 플랜 이상(스탠다드·프로·관리자) 여부 */
export const isStandardPlanOrAbove = (userType?: string): boolean =>
  userType === 'standard' || userType === 'pro' || userType === 'admin';

/**
 * 멤버 개별 페이지 접근 가능 여부 체크 (스탠다드/프로만 가능, 프리·베이직 불가)
 */
/** 과제 제출 이미지 — 스탠다드/프로/관리자만 */
export const checkAssignmentImageSubmissionAccess = (userType: string): { allowed: boolean; message?: string } => {
  if (userType === 'free' || userType === 'basic') {
    return {
      allowed: false,
      message: '과제 이미지 제출은 스탠다드 플랜 이상에서 이용할 수 있습니다.'
    };
  }
  return { allowed: true };
};

export const checkMemberPageAccess = (userType: string): { allowed: boolean; message?: string } => {
  if (userType === 'free') {
    return {
      allowed: false,
      message: '코디가 프리 플랜을 이용중이므로 멤버 개별 페이지 이용이 불가합니다.',
    };
  }
  if (userType === 'basic') {
    return {
      allowed: false,
      message: '멤버 개별 페이지 접근은 스탠다드·프로 플랜에서만 이용 가능합니다.',
    };
  }
  return { allowed: true };
};

/**
 * 알람 세트 수 제한 체크
 */
export const checkAlarmSetLimit = (
  user: User,
  currentSetCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxAlarmSets === null) return { allowed: true };
  
  if (currentSetCount >= limits.maxAlarmSets) {
    return {
      allowed: false,
      message: `최대 ${limits.maxAlarmSets}개 알람 세트까지만 생성할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/**
 * 알람 카테고리 접근 가능 여부 체크
 */
export const checkAlarmCategoryAccess = (
  user: User,
  category: 'basic' | 'class'
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  
  if (category === 'basic') {
    // 기본 알람은 모든 플랜에서 접근 가능
    return { allowed: true };
  }
  
  // 수업 알람 및 기타 카테고리는 허용된 카테고리 목록에 있는지 확인
  if (!limits.allowedAlarmCategories.includes(category)) {
    return {
      allowed: false,
      message: '프리 플랜에서는 기본 알람만 사용할 수 있습니다.',
    };
  }
  
  return { allowed: true };
};

/**
 * 보드 공지 저장 가능 여부 체크 (전체공지, 개별공지) — 스탠다드 이상만 가능
 */
export const checkBoardNoticeAccess = (user: User): { allowed: boolean; message?: string } => {
  if (user.userType === 'free') {
    return {
      allowed: false,
      message: '프리 플랜에서는 전체공지와 개별공지를 저장할 수 없습니다. 이용권을 업그레이드하세요.',
    };
  }
  if (user.userType === 'basic') {
    return {
      allowed: false,
      message: '전체공지/개별공지 저장은 스탠다드 플랜 이상에서 이용 가능합니다.',
    };
  }
  return { allowed: true };
};

/**
 * 노트 카테고리 수 제한 체크
 */
export const checkNoteCategoryLimit = (
  user: User,
  currentCategoryCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  if (limits.maxNoteCategories === null) return { allowed: true };
  
  if (currentCategoryCount >= limits.maxNoteCategories) {
    return {
      allowed: false,
      message: `최대 ${limits.maxNoteCategories}개 노트 카테고리까지만 생성할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

/**
 * 멤버 그룹 개수 제한 체크 (플랜별: 프리/베이직 1개, 스탠다드 2개, 프로/관리자 15개)
 */
export const checkMemberGroupAccess = (
  user: User,
  currentGroupCount: number
): { allowed: boolean; message?: string } => {
  const limits = getLicenseLimits(user.userType);
  const maxGroups = limits.maxMemberGroups ?? 1;
  if (currentGroupCount > maxGroups) {
    return {
      allowed: false,
      message: `현재 플랜에서는 최대 ${maxGroups}개 멤버 그룹까지만 사용할 수 있습니다. 이용권을 업그레이드하세요.`,
    };
  }
  return { allowed: true };
};

